import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import useFetch, { unsafeAPI } from '../hooks/api';
import '../liveness.css'; // Import custom CSS
import Spinner, { ContainerSpinner } from './Spinner';
import { useEffect, useState } from 'react';
import { cn } from '../shadcn/utils';

export default function LivenessSession({
	userIdentificationNumber,
	livenessSessionId,
	livenessDocumentId,
	userIdentificationIdUrl,
	isNewUser,
	origin,
	onSuccess,
	onError,
	onReady,
}: {
	userIdentificationNumber: string;
	livenessSessionId?: string;
	livenessDocumentId?: string;
	userIdentificationIdUrl?: string;
	isNewUser?: boolean;
	origin?: string;
	onSuccess?: (valid_user: boolean, user_authenticated: boolean) => void;
	onError?: (error?: string) => void;
	onReady?: () => void;
}) {
	const [cameraPermissionGranted, setCameraPermissionGranted] = useState<boolean>(false);
	const [state, setState] = useState<
		| 'checking-camera-permissions' // Step 1
		| 'in-progress' // Step 2
		| 'submitting-results' // Step 3
		| 'checking-results' // Step 4
		| 'session-success' // Step 5
		| 'session-error' // Error
		| 'camera-permission-denied' // Error
	>('checking-camera-permissions');

	// Conditionally initialize session data if camera permissions are granted and session data was not pre-provided
	const { data: sessionData, error: initializationError } = useFetch(
		'https://api.krece.app/authentication/liveness-detection-session/',
		'GET',
		cameraPermissionGranted && !livenessSessionId, // Only fetch when camera permission is granted
		{ user_identification_number: userIdentificationNumber, ...(livenessDocumentId && { document_id: livenessDocumentId }) },
		undefined,
		false // No auth token required
	);
	if (initializationError) {
		setState('session-error');
		if (onError) onError();
	}

	// Step 1: Get camera permissions on component mount
	useEffect(() => {
		let stream: MediaStream;

		(async () => {
			try {
				// Request camera permissions
				stream = await navigator.mediaDevices.getUserMedia({ video: true });

				// If successful, stop the stream and set permission as granted
				stream.getTracks().forEach((track) => track.stop());

				setCameraPermissionGranted(true);
			} catch (error) {
				console.error('Camera permission denied:', error);
				setState('camera-permission-denied');
				if (onError) onError();
			}
		})();

		// Cleanup function to stop the stream when the component unmounts
		return () => {
			if (stream) stream.getTracks().forEach((track) => track.stop());
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Step 2: Update the state to in-progress when we have session data and camera permissions are granted
	useEffect(() => {
		if (!cameraPermissionGranted || state !== 'checking-camera-permissions') return;

		if (sessionData?.session_id || livenessSessionId) {
			setState('in-progress');
			if (onReady) onReady();
		}
	}, [sessionData?.session_id, state, cameraPermissionGranted, livenessSessionId, onReady]);

	// Step 3: Detect when session is submitting results
	useEffect(() => {
		const observer = new MutationObserver(() => {
			const hintElement = document.querySelector('.amplify-liveness-hint');
			const container = document.querySelector('.liveness-container');

			if (hintElement && container && hintElement.textContent?.includes('Verificación completa')) {
				setState('submitting-results');
			}
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
			characterData: true
		});

		return () => observer.disconnect();
	}, []);

	// Step 4: Send the results to the BE
	async function handleAnalysisComplete() {
		setState('checking-results');

		try {
			const response = await unsafeAPI(
				'POST',
				'https://api.krece.app/authentication/liveness-detection-session/',
				{
					document_id: sessionData?.document_id || livenessDocumentId,
					liveness_session_id: sessionData?.session_id || livenessSessionId,
					user_identification_number: userIdentificationNumber,
					...(userIdentificationIdUrl && { photo_id_url: userIdentificationIdUrl }),
					...(isNewUser && { is_new_user: isNewUser }),
					...(origin && { origin: origin })
				},
				null,
				null,
				false
			);

			if (
				response?.success === true &&
				response?.data?.user_authenticated === true &&
				response?.data?.valid_user === true &&
				response?.data?.photo_id_url
			) {
				setState('session-success');
				if (onSuccess) onSuccess(response.data.valid_user, response.data.user_authenticated);
			} else if (
				response?.success === true &&
				response?.data?.user_authenticated === true &&
				response?.data?.valid_user === false
			) {
				setState('session-error');
				if (onError) onError();
			} else {
				setState('session-error');
				if (onError) onError();
			}
		} catch (error) {
			setState('session-error');
			if (onError) onError();
		}
	}

	// Detect and handle errors
	function handleSessionError(error: any) {
		setState('session-error');
		if (onError) onError();
	}

	useEffect(() => {
		if (sessionData?.link_expired === true) {
			setState('session-error');
			if (onError) onError('link-expired');
		}
	}, [sessionData?.link_expired, onError]);

	if (state === 'checking-camera-permissions') {
		return (
			<div className="flex h-full min-h-[200px] w-full items-center justify-center">
				<Spinner size={3} />
			</div>
		);
	} else if (state === 'camera-permission-denied') {
		return (
			<div className="flex h-full min-h-[200px] w-full flex-col items-center justify-center">
				<div className="text-center">
					<p className="mb-2 font-medium text-red-600">Permisos de cámara requeridos</p>
					<p className="text-sm text-gray-600">
						Para continuar con la verificación, necesitas otorgar permisos de acceso a la cámara.
					</p>
				</div>
			</div>
		);
	} else if (state === 'in-progress' || state === 'submitting-results' || state === 'checking-results') {
		return (
			<div className="relative">
				<div
					className={cn('liveness-container', (state === 'submitting-results' || state === 'checking-results') && 'opacity-0')}
				>
					<FaceLivenessDetector
						components={{
							PhotosensitiveWarning: (): React.JSX.Element => {
								return <></>;
							}
						}}
						displayText={{
							cameraMinSpecificationsHeadingText: 'La cámara no cumple los requisitos mínimos',
							cameraMinSpecificationsMessageText: 'La cámara debe soportar al menos 320x240 de resolución y 15 fps.',
							cameraNotFoundHeadingText: 'No se detecta la cámara',
							cameraNotFoundMessageText:
								'Verifica que la cámara esté conectada y que ninguna otra app la esté usando. Revisa los permisos y reinicia el navegador si es necesario.',
							a11yVideoLabelText: 'Cámara para verificación de vida',
							cancelLivenessCheckText: 'Cancelar verificación',
							goodFitCaptionText: 'Buen encuadre',
							goodFitAltText: 'Ilustración de un rostro encajando perfectamente en un óvalo.',
							hintCenterFaceText: 'Centra tu rostro',
							hintCenterFaceInstructionText:
								'Antes de empezar, asegúrate de que la cámara esté centrada arriba de la pantalla y tu rostro en el centro. Cuando inicie, aparecerá un óvalo. Acércate y mantente quieto cuando se indique. Al terminar escucharás "verificación completa".',
							hintFaceOffCenterText: 'Rostro fuera del óvalo, céntralo.',
							hintMoveFaceFrontOfCameraText: 'Pon tu rostro frente a la cámara',
							hintTooManyFacesText: 'Solo debe haber un rostro frente a la cámara',
							hintFaceDetectedText: 'Rostro detectado',
							hintCanNotIdentifyText: 'Pon tu rostro frente a la cámara',
							hintTooCloseText: 'Aléjate un poco',
							hintTooFarText: 'Acércate un poco',
							hintConnectingText: 'Conectando...',
							hintVerifyingText: 'Verificando...',
							hintCheckCompleteText: 'Verificación completa',
							hintIlluminationTooBrightText: 'Busca un lugar menos iluminado',
							hintIlluminationTooDarkText: 'Busca un lugar más iluminado',
							hintIlluminationNormalText: 'Iluminación adecuada',
							hintHoldFaceForFreshnessText: 'Mantente quieto',
							hintMatchIndicatorText: '50% completado. Sigue acercándote.',
							photosensitivityWarningBodyText: 'Esta verificación muestra luces de colores. Ten precaución si eres fotosensible.',
							photosensitivityWarningHeadingText: 'Advertencia de fotosensibilidad',
							photosensitivityWarningInfoText:
								'Algunas personas pueden tener convulsiones con luces de colores. Ten precaución si tú o tu familia tienen epilepsia.',
							photosensitivityWarningLabelText: 'Más información sobre fotosensibilidad',
							photosensitivyWarningBodyText: 'Esta verificación muestra luces de colores. Ten precaución si eres fotosensible.',
							photosensitivyWarningHeadingText: 'Advertencia de fotosensibilidad',
							photosensitivyWarningInfoText:
								'Algunas personas pueden tener convulsiones con luces de colores. Ten precaución si tú o tu familia tienen epilepsia.',
							photosensitivyWarningLabelText: 'Más información sobre fotosensibilidad',
							retryCameraPermissionsText: 'Reintentar',
							recordingIndicatorText: 'Rec',
							startScreenBeginCheckText: 'Iniciar verificación',
							tooFarCaptionText: 'Demasiado lejos',
							tooFarAltText: 'Ilustración de un rostro dentro de un óvalo; hay un espacio entre el rostro y el borde del óvalo.',
							waitingCameraPermissionText: 'Esperando permiso de cámara...',
							errorLabelText: 'Error',
							connectionTimeoutHeaderText: 'Tiempo de conexión agotado',
							connectionTimeoutMessageText: 'La conexión ha expirado.',
							timeoutHeaderText: 'Tiempo agotado',
							timeoutMessageText:
								'No lograste encajar el rostro en el óvalo a tiempo. Intenta de nuevo y llena el óvalo con tu rostro.',
							faceDistanceHeaderText: 'Movimiento detectado',
							faceDistanceMessageText: 'Evita acercarte durante la conexión.',
							multipleFacesHeaderText: 'Se detectaron varios rostros',
							multipleFacesMessageText: 'Solo debe haber un rostro frente a la cámara al conectar.',
							clientHeaderText: 'Error del cliente',
							clientMessageText: 'La verificación falló por un problema local',
							serverHeaderText: 'Error del servidor',
							serverMessageText: 'No se puede completar la verificación por un problema del servidor',
							landscapeHeaderText: 'No se admite modo horizontal',
							landscapeMessageText: 'Gira tu dispositivo a vertical.',
							portraitMessageText: 'Mantén tu dispositivo en vertical durante la verificación.',
							tryAgainText: 'Intentar de nuevo'
						}}
						sessionId={(sessionData?.session_id || livenessSessionId)!}
						region="us-east-1"
						onAnalysisComplete={handleAnalysisComplete}
						onError={handleSessionError}
						disableStartScreen={true}
						onUserCancel={() => {
							setState('session-error');
							if (onError) onError();
						}}
					/>
				</div>
				{(state === 'submitting-results' || state === 'checking-results') && (
					<div className="absolute inset-0 h-full w-full">
						<ContainerSpinner size={3} />
					</div>
				)}
			</div>
		);
	} else {
		return <></>;
	}
}
